import { DEFAULT_GROUPS_SORT_BY } from "@components/groups/groups-constants";

/** Defines the minimum length for the group name */
export const MIN_GROUP_NAME_LENGTH = 1;

/** Returns true if a group should have a demo label. */
export function isGroupDemo(group) {
  return group.name === "Demo";
}

/** Get number of projects in a group */
export function getProjectNumbersInGroup(group) {
  return group.numProjects ?? 0;
}

/**
 * Returns the group members that the workspace is allowed to see. When it cannot assign
 * managers, group managers are hidden so only viewers remain.
 */
export function getVisibleGroupMembers(members, canAssignManagers) {
  return canAssignManagers ? members : members.filter(member => member.role === "GROUP_VIEWER");
}

/** Counts of group members grouped by their group role. */

/** Counts the group members that hold each group role. */
export function countGroupMembersByRole(members) {
  return {
    groupManagers: countMembersWithRole(members, "COMPANY_MANAGER"),
    projectManagers: countMembersWithRole(members, "PROJECT_MANAGER"),
    viewers: countMembersWithRole(members, "GROUP_VIEWER")
  };
}

/** Counts the group members that hold the given group role. */
function countMembersWithRole(members, role) {
  return members.filter(member => member.role === role).length;
}

/**
 * Rank of a member for the order in which a group is emptied. The backend refuses to unassign a
 * group manager while members of any other role are still assigned, so managers rank last and
 * anything that is not a manager ranks first.
 */
function getRemovalRank(member) {
  switch (member.role) {
    case "COMPANY_MANAGER":
      return 2;
    case "PROJECT_MANAGER":
      return 1;
    default:
      return 0;
  }
}

/**
 * Orders group members so that unassigning them one by one empties the group without the
 * backend rejecting a request: viewers first, then project managers, then group managers. The
 * current user goes last within their own rank, as a group manager who unassigns themselves
 * loses the permission for every request that follows.
 */
export function sortGroupMembersByRemovalOrder(members, currentUserIdentity) {
  return [...members].sort((memberA, memberB) => {
    const rankDifference = getRemovalRank(memberA) - getRemovalRank(memberB);
    if (rankDifference !== 0) {
      return rankDifference;
    }
    return Number(memberA.identity === currentUserIdentity) - Number(memberB.identity === currentUserIdentity);
  });
}

/**
 * Gets the member count for a group
 * @param group - The group to get the member count from
 * @returns The number of members in the group, or -1 if the member count cannot be determined
 * @remarks
 * - For GroupSummaryResponse: returns the numManagers property if available
 * - For GroupDetailResponse: returns the length of the members array
 * - Returns -1 if the member count cannot be determined (properties not found or undefined)
 */
export function getMemberCount(group) {
  if ("numManagers" in group && group.numManagers !== undefined) {
    return group.numManagers;
  }
  if ("members" in group && Array.isArray(group.members)) {
    return group.members.length;
  }
  return -1;
}

/**
 * Sorts an array of groups based on the provided sorting criteria
 * @param groups - The array of groups to sort
 * @param sortBy - The sorting criteria
 * @returns A new sorted array of groups
 */
export function sortGroups(groups, sortBy) {
  const sortedGroups = [...groups];

  // Pre-compute timestamps for creation date sorting to avoid repeated parsing
  const createdAtMap = sortBy === "created_date_ascending" || sortBy === "created_date_descending" ? new Map(groups.map(group => [group.id, new Date(group.createdAt).getTime()])) : null;
  sortedGroups.sort((a, b) => {
    switch (sortBy) {
      case "name_ascending":
        return a.name.localeCompare(b.name);
      case "name_descending":
        return b.name.localeCompare(a.name);
      case "members_ascending":
        {
          const aMemberCount = getMemberCount(a);
          const bMemberCount = getMemberCount(b);
          return aMemberCount - bMemberCount;
        }
      case "members_descending":
        {
          const aMemberCount = getMemberCount(a);
          const bMemberCount = getMemberCount(b);
          return bMemberCount - aMemberCount;
        }
      case "projects_ascending":
        {
          const aProjectCount = getProjectNumbersInGroup(a);
          const bProjectCount = getProjectNumbersInGroup(b);
          return aProjectCount - bProjectCount;
        }
      case "projects_descending":
        {
          const aProjectCount = getProjectNumbersInGroup(a);
          const bProjectCount = getProjectNumbersInGroup(b);
          return bProjectCount - aProjectCount;
        }
      case "created_date_ascending":
        return createdAtMap.get(a.id) - createdAtMap.get(b.id);
      case "created_date_descending":
        return createdAtMap.get(b.id) - createdAtMap.get(a.id);
      default:
        return 0;
    }
  });
  return sortedGroups;
}

/**
 * Converts a SortItem to a GroupsSortBy string
 * @param sortItem The sort item containing field and sort direction
 * @returns The GroupsSortBy string
 */
export function getGroupsSortBy(sortItem) {
  const {
    field,
    sort
  } = sortItem;
  const isAscending = sort === "asc";

  // Map the field values to the GroupsSortBy keys
  switch (field) {
    case "name":
      return isAscending ? "name_ascending" : "name_descending";
    case "members":
      return isAscending ? "members_ascending" : "members_descending";
    case "projects":
      return isAscending ? "projects_ascending" : "projects_descending";
    case "createdDate":
      return isAscending ? "created_date_ascending" : "created_date_descending";
    default:
      {
        return DEFAULT_GROUPS_SORT_BY;
      }
  }
}